import type { AIConfig, AIProvider, FeedbackRequest } from "./types";
import { AIError } from "./types";
import { buildSystemPrompt, buildUserPrompt } from "./prompt";

const API_URL = "https://api.anthropic.com/v1/messages";
const API_VERSION = "2023-06-01";

interface AnthropicMessage {
  role: "user" | "assistant";
  content: string;
}

function humanizeError(status: number, body: string): string {
  // Try to surface Anthropic's own error message when present.
  let detail = "";
  try {
    const parsed = JSON.parse(body) as { error?: { message?: string } };
    detail = parsed.error?.message ?? "";
  } catch {
    /* non-JSON body */
  }
  switch (status) {
    case 400:
      return detail || "The request was rejected. Check the selected model.";
    case 401:
      return "Invalid API key. Check it in Settings.";
    case 403:
      return "This API key isn't allowed to use that model.";
    case 404:
      return "Model not found — pick a different one in Settings.";
    case 429:
      return "Rate limit reached. Wait a moment and try again.";
    case 529:
      return "Anthropic is overloaded right now. Try again shortly.";
    default:
      return detail || `Request failed (HTTP ${status}).`;
  }
}

export function createAnthropicProvider(cfg: AIConfig): AIProvider {
  async function call(
    messages: AnthropicMessage[],
    system: string | undefined,
    maxTokens: number,
    signal?: AbortSignal
  ): Promise<string> {
    let res: Response;
    try {
      res = await fetch(API_URL, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "x-api-key": cfg.apiKey,
          "anthropic-version": API_VERSION,
          // Required to call the API directly from a browser.
          "anthropic-dangerous-direct-browser-access": "true",
        },
        body: JSON.stringify({ model: cfg.model, max_tokens: maxTokens, system, messages }),
        signal,
      });
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") throw e;
      throw new AIError("Couldn't reach Anthropic. Check your connection.");
    }

    if (!res.ok) {
      const body = await res.text().catch(() => "");
      throw new AIError(humanizeError(res.status, body));
    }

    const data = (await res.json()) as { content?: { type: string; text?: string }[] };
    const text = (data.content ?? [])
      .filter((b) => b.type === "text" && b.text)
      .map((b) => b.text as string)
      .join("\n")
      .trim();
    if (!text) throw new AIError("The model returned an empty response.");
    return text;
  }

  return {
    id: "anthropic",
    label: "Anthropic (Claude)",
    feedback: (req: FeedbackRequest, signal?: AbortSignal) =>
      call([{ role: "user", content: buildUserPrompt(req) }], buildSystemPrompt(req.kind), 1024, signal),
    ask: (system: string | undefined, user: string, maxTokens = 1024, signal?: AbortSignal) =>
      call([{ role: "user", content: user }], system, maxTokens, signal),
    test: async (signal?: AbortSignal) => {
      await call([{ role: "user", content: "Reply with the single word: ok" }], undefined, 8, signal);
    },
  };
}
